/**
 * Which routes suppress the global date-range picker in the header.
 *
 * The picker is a promise: change it and the page beneath re-reads its data over the new
 * range. A page that ignores `dateRange` breaks that promise silently — the control moves,
 * nothing happens, and the reader is left deciding whether the page is broken or the
 * numbers are stale. Worse on Search Console, whose own copy names a different window, so
 * the header and the page appear to disagree about what is on screen.
 *
 * Each entry names a page whose data does NOT come from the global range:
 *
 *  - `/weather-intelligence` — has its own As-of snapshot picker, so a global range
 *    alongside it would mean two different things at once.
 *  - `/hub` — the internal-ops hub (Effort, Now, Clients, …). Each hub page carries its own
 *    controls (Effort its own range selector, Now its consequence-ordered feed, Clients its
 *    list) and reads none of its data from the global range; the org-level picker only sits
 *    on top of them saying almost the same thing. Prefix-matched, so every `/hub/*` surface
 *    inherits this.
 *  - `/my-work` — Portal "MY WORK" reads `fn_get_portal` over a `date_range: "live"` window
 *    (the work-in-flight right now), so the global date-range picker drives nothing on this
 *    page and is hidden (Epic 3).
 *
 * Lives in its own module because the rule was previously inlined in both `AdminHeader`
 * and `ClientHeader`, and drifted every time it changed. At the point this was extracted
 * `ClientHeader` carried only the Competitive Intelligence case — it had never picked up
 * AI Visibility, and did not pick up Weather Intelligence either — so client-role readers
 * saw a dead picker on both. One list, imported twice, is what stops the next page
 * repeating it.
 */
export const NO_GLOBAL_DATE_RANGE_ROUTES = [
  '/weather-intelligence',
  '/hub',
  '/my-work',
];

/**
 * True when the header should hide its global date-range picker for this path.
 *
 * @param {string} pathname `location.pathname` — matched by prefix, so nested routes
 *   (`/search-console/anything`) inherit their parent's answer.
 * @returns {boolean}
 */
export function hidesGlobalDateRange(pathname) {
  if (typeof pathname !== 'string') return false;
  return NO_GLOBAL_DATE_RANGE_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Routes where the global date-range picker is rendered DISABLED (greyed,
 * non-interactive) rather than hidden, because the page beneath owns its own
 * date window and the global range would otherwise disagree with it.
 *
 * Distinct from `NO_GLOBAL_DATE_RANGE_ROUTES` (full hide) — here we keep the
 * control visible so the header layout doesn't shift, but lock it and show a
 * hint explaining why.
 *
  *  - `/competitive-intelligence` — forward-looking (next 90 days from the latest
 *    sync). Its RPCs (`ci_get_rate_calendar`, `ci_report_forward_calendar`) accept no
 *    start/end date params, so the global range would silently disagree with the page;
 *    the picker is disabled with a hint rather than hidden to keep the header layout
 *    stable across routes (Epic 5).
 *  - `/search-console` — reads a nightly snapshot over a fixed 90-day window decided by
 *    the backend. The page carries its own admin-only As-of picker and its own Detail
 *    range control; the global one drives nothing and offers dates beyond the data, so
 *    it is disabled with a hint rather than hidden to keep the header layout stable
 *    (Epic 3).
 *  - `/ai-visibility` — AI Monitor is built around scheduled AI-visibility runs, not a
 *    date range; the page owns its own Run + Compare-to run selectors. Disabled with a
 *    hint rather than hidden to keep the header layout stable across routes (Epic 2).
 */
export const DISABLE_GLOBAL_DATE_RANGE_ROUTES = [
  '/competitive-intelligence',
  '/search-console',
  '/ai-visibility',
];

/**
 * True when the header should render its global date-range picker DISABLED for
 * this path (the control stays visible but is non-interactive, with a hint).
 *
 * @param {string} pathname `location.pathname` — matched by prefix, so nested
 *   routes (`/competitive-intelligence/anything`) inherit their parent's answer.
 * @returns {boolean}
 */
export function disablesGlobalDateRange(pathname) {
  if (typeof pathname !== 'string') return false;
  return DISABLE_GLOBAL_DATE_RANGE_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Per-route tooltip text shown when the global picker is DISABLED.
 *
 * This is the single source of truth for *why* the picker is locked on each
 * disabled route — previously the hint string was hardcoded in both
 * `AdminHeader` and `ClientHeader`, which drifted (ClientHeader only ever
 * carried the Event Intelligence message and missed every other case). One
 * lookup, imported by both headers, is what stops the next disabled route
 * from shipping with the wrong hint or no hint at all.
 *
 * Keyed by the route prefix exactly as it appears in
 * `DISABLE_GLOBAL_DATE_RANGE_ROUTES`. The resolver (`getDatePickerState`)
 * matches the prefix the same way `disablesGlobalDateRange` does, so a nested
 * route inherits its parent's reason.
 *
 * Every route in `DISABLE_GLOBAL_DATE_RANGE_ROUTES` must have an entry here — a
 * disabled picker with no hint is a dead control.
 */
export const DISABLE_REASONS = {
  '/competitive-intelligence':
    'Competitive Intelligence is forward-looking — the date range is determined by the latest data sync.',
  '/search-console':
    'Search Console reads a fixed 90-day nightly snapshot — use the As-of picker below.',
  '/ai-visibility':
    'AI Monitor is built around scheduled AI-visibility runs — pick a run below, not a date range.',
};

/**
 * Resolve the global date-range picker's state for a given path.
 *
 * The single entry point both headers should call instead of composing
 * `hidesGlobalDateRange` + `disablesGlobalDateRange` + a hand-written hint
 * string. Returns a plain object so callers destructure once and feed the
 * three values (`hidden`/`disabled` booleans + `hint` string) straight into
 * `<DateRangePicker disabled={…} hint={…} />`.
 *
 * Precedence: **hidden beats disabled**. A route in `NO_GLOBAL_DATE_RANGE_ROUTES`
 * gets `{ state: 'hidden' }` with no reason (the picker isn't rendered, so a
 * tooltip would never show). Only a route in `DISABLE_GLOBAL_DATE_RANGE_ROUTES`
 * (and NOT in the hidden list) gets `{ state: 'disabled', disabledReason }`.
 * Everything else is `{ state: 'active' }`.
 *
 * Route Intelligence is in neither list: every one of its tabs re-reads its
 * data over the global range, so the picker is active on all of them.
 *
 * @param {string} pathname `location.pathname` — prefix-matched, so nested
 *   routes inherit their parent's answer.
 * @returns {{ state: 'active' | 'hidden' | 'disabled', disabledReason?: string }}
 */
export function getDatePickerState(pathname) {
  if (typeof pathname !== 'string' || pathname === '') {
    return { state: 'active' };
  }
  // Hidden takes precedence — the nav-bar slot is reused by a page-local
  // control (Weather's As-of picker), so the global picker is absent, not
  // merely locked. A disabled tooltip would never render on a hidden route.
  if (hidesGlobalDateRange(pathname)) {
    return { state: 'hidden' };
  }
  if (disablesGlobalDateRange(pathname)) {
    const route = DISABLE_GLOBAL_DATE_RANGE_ROUTES.find((r) => pathname.startsWith(r));
    return {
      state: 'disabled',
      disabledReason: route ? (DISABLE_REASONS[route] || null) : null,
    };
  }
  return { state: 'active' };
}
